import { ui_service_ip, intelligence_ip } from "./ip_details";
import { call_post_api, call_get_api, call_delete_api, call_update_api } from "./event_handling";
/**
 * Generic function to handle API calls based on the method type.
 * @param {string} method - The HTTP method (GET, POST, UPDATE, DELETE).
 * @param {string} endpoint - The endpoint for the API call.
 * @param {Object} params - The parameters or request body for the API call.
 * @returns {Object} - The result from the API call.
 */
async function handleApiCall(method, endpoint, params) {
    try {
        let result;
        switch (method.toUpperCase()) {
            case "GET":
                result = await call_get_api(ui_service_ip, endpoint, params);
                break;

            case "POST":
                result = await call_post_api(ui_service_ip, endpoint, params);
                break;

            case "PUT":
            case "UPDATE":
                result = await call_update_api(ui_service_ip, endpoint, params);
                break;

            case "DELETE":
                result = await call_delete_api(ui_service_ip, endpoint, params);
                break;

            default:
                throw new Error(`Unsupported HTTP method: ${method}`);
        }

        return result; // Return the result from the API
    } catch (error) {
        console.error(`Error during ${method} request to ${endpoint}:`, error); // Log any errors
    }
}

/**
 * Function to fetch the latest environment list (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getLatestEnvironmentList(requestParams) {
    return handleApiCall("GET", "get_environments_latest", requestParams);
}

/**
 * Function to fetch the watchlist (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getWatchList(requestParams) {
    return handleApiCall("GET", "get-watchlist", requestParams);
}

/**
 * Function to fetch all environment integrations (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAllEnvironmentIntegrations(requestParams) {
    return handleApiCall("GET", "env/list-integrations", requestParams);
}

/**
 * Function to fetch metrics value from the real-time monitoring tree (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getLeftPanelTreeData(requestParams) {    
    return handleApiCall("GET", "/monitor/verticalization", requestParams);
}

/**
 * Function to fetch chart data for real-time monitoring (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getChartData(requestParams) {
    return handleApiCall("GET", "monitor/realtime", requestParams);
}

export function getKpiAggregation(requestParams) {
    return handleApiCall("GET", "monitor/realtime/kpi_aggregation", requestParams);
}

/**
 * Data assets and per-tier (GOLD/SILVER/BRONZE) issue breakdown for a KPI.
 * @param {Object} requestParams - { tenant_id, env_id, kpi_id }
 */
export function getKpiDataAssets(requestParams) {
    return handleApiCall("GET", "/monitor/kpi_data_assets", requestParams);
}

/**
 * Function to fetch metrics aggregation data (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getMetricsAggregation(requestParams) {
    return handleApiCall("GET", "monitor/realtime/aggregation", requestParams);
}

/**
 * Function to fetch all metadata mappings (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getAllMetaData(requestParams) {
    return handleApiCall("GET", "business_mapping", requestParams);
}

/**
 * Function to save data to the API (POST example).
 * @param {Object} requestBody - The request body for the API call.
 * @returns {Object} - The result from the API call.
 */
export function saveWatchList(requestBody) {
    return handleApiCall("POST", "save-watchlist", requestBody);
}

/**
 * Function to delete data from the API (DELETE example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function deleteWatchList(requestParams) {
    return handleApiCall("DELETE", "delete-watchlist", requestParams);
  
}

/**
 * Function to edit the watchlist (GET example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getEditWatchList(requestParams) {
    return handleApiCall("GET", "edit-watchlist", requestParams);

}

/**
 * Function to update data in the API (PUT/UPDATE example).
 * @param {Object} requestBody - The request body for the API call.
 * @returns {Object} - The result from the API call.
 */
export function updateWatchList(requestBody) {
    return handleApiCall("UPDATE", "edit-watchlist", requestBody);

}

/**
 * Function to delete data from the API (DELETE example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function deleteUserTracking(requestParams) {
    return handleApiCall("DELETE", "user_tracking", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function addUserTracking(requestParams) {
    return handleApiCall("POST", "user_tracking", requestParams);
}

/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getIncidents(requestParams) {
    return handleApiCall("GET", "incidents", requestParams);
}

export function getVerticalAllIncidents(requestParams) {
    return handleApiCall("GET", "verticals/all_incidents", requestParams);
}

export function getVerticalHeroIncidents(requestParams) {
    return handleApiCall("GET", "verticals/hero_incidents", requestParams);
}

export function getVerticalKpiSummaryData(requestParams) {
    return handleApiCall("GET", "verticals/get_kpi_summary_data", requestParams);
}

export function getVerticalBusinessAreaIncidents(requestParams) {
    return handleApiCall("GET", "verticals/all_incidents/business_area", requestParams);
}

export function postVerticalInteraction(requestParams) {
    return handleApiCall("POST", "verticals/interactions", requestParams);
}

export function getVerticalAiInsights(requestParams, method = "GET") {
    // Critical: GET ui-api-service/verticals/incidents/ai_insights
    // Non-Critical: POST intelligence/assistant/ai_insights
    if (String(method).toUpperCase() === "POST") {
        return call_post_api(intelligence_ip, "assistant/ai_insights", requestParams);
    }
    return call_get_api(ui_service_ip, "verticals/incidents/ai_insights", requestParams);
}

export function getIncidentAiInsights(requestParams) {
    // POST intelligence/assistant/incident_ai_insights
    // { dataquality_incident_id } for DQ incidents, { pipeline_incident_id } for pipeline incidents
    return call_post_api(intelligence_ip, "assistant/incident_ai_insights", requestParams);
}

export function getVerticalDiagnosticMap(requestParams) {
    return handleApiCall("GET", "verticals/incidents/diagnostic_map", requestParams);
}


/**
 * Function to add data from the API (POST example).
 * @param {Object} requestParams - The request parameters for the API call.
 * @returns {Object} - The result from the API call.
 */
export function getWebSocketUrl(requestParams) {
    return handleApiCall("GET", "get_ws_string", requestParams);
}

export function getProgressInfo(requestParams){
    return handleApiCall("GET", "dataset_progress_info", requestParams);
}
export function getIntegration(requestParams){
    return handleApiCall("GET", "integrations", requestParams);
}
export function getPipelineIntegration(requestParams){
    return handleApiCall("GET", "pipelineintegrations", requestParams);
}

export function getPipeline(requestParams){
    return handleApiCall("GET", "pipeline/realtime", requestParams);
}

export function getPipelineJobInfo(requestParams){
    return handleApiCall("GET", "realtime/pipeline_info", requestParams);
}

export function getLineageData(requestParams){
    return handleApiCall("GET", "lineage_data", requestParams);
}

export function getPipelineMetadata(requestParams){
    return handleApiCall("GET", "pipeline_mapping", requestParams);
}


export function pipelineRunInfo(requestParams){
    return handleApiCall("GET", "realtime/runs_info", requestParams);
}

export function pipelineStagesInfo(requestParams){
    return handleApiCall("GET", "realtime/stages_info", requestParams);
}





